import { storage } from '@guardian/libs';
import { mergeCalls } from 'common/modules/async-call-merger';
import { createAuthenticationComponentEventParams } from 'common/modules/identity/auth-component-event-params';
import config_ from 'lib/config';
import { getCookie as getCookieByName } from 'lib/cookies';
import { fetchJson } from 'lib/fetch-json';
import mediator from 'lib/mediator';
import { getUrlVars } from 'lib/url';
// This is really a hacky workaround ⚠️
const config = config_;
let userFromCookieCache = null;
const cookieName = 'GU_U';
const signOutCookieName = 'GU_SO';
const fbCheckKey = 'gu.id.nextFbCheck';
const idApiRoot = config.get('page.idApiUrl');
const profileRoot = config.get('page.idUrl');
mediator.emit('module:identity:api:loaded');
export const decodeBase64 = (str) => decodeURIComponent(escape(window.atob(str.replace(/-/g, '+').replace(/_/g, '/').replace(/,/g, '='))));
export const getUserFromCookie = () => {
    if (userFromCookieCache === null) {
        const cookieData = getCookieByName(cookieName);
        let userData = null;
        if (cookieData) {
            userData = JSON.parse(decodeBase64(cookieData.split('.')[0]));
        }
        if (userData) {
            const displayName = decodeURIComponent(userData[2]);
            userFromCookieCache = {
                id: userData[0],
                primaryEmailAddress: userData[1],
                publicFields: {
                    displayName,
                },
                dates: { accountCreatedDate: userData[6] },
                statusFields: {
                    userEmailValidated: Boolean(userData[7]),
                },
                rawResponse: cookieData,
            };
        }
    }
    return userFromCookieCache;
};
export const updateNewsletter = (newsletter) => {
    const url = `${idApiRoot}/users/me/newsletters`;
    return fetch(url, {
        method: 'PATCH',
        credentials: 'include',
        mode: 'cors',
        body: JSON.stringify(newsletter),
    }).then(() => Promise.resolve());
};
export const buildNewsletterUpdatePayload = (action = 'none', newsletterId) => {
    const newsletter = { id: newsletterId };
    switch (action) {
        case 'add':
            newsletter.subscribed = true;
            break;
        case 'remove':
            newsletter.subscribed = false;
            break;
        default:
            throw new Error(`Undefined newsletter action type (${action})`);
    }
    return newsletter;
};
export const isUserLoggedIn = () => getUserFromCookie() !== null;
export const getUserFromApi = mergeCalls((mergingCallback) => {
    if (isUserLoggedIn()) {
        const url = `${idApiRoot}/user/me`;
        void fetchJson(url, {
            mode: 'cors',
            credentials: 'include',
        }) // assert unknown -> IdentityResponse
            .then((data) => {
            if (data.status === 'ok') {
                mergingCallback(data.user);
            }
            else {
                mergingCallback(null);
            }
        });
    }
    else {
        mergingCallback(null);
    }
});
export const reset = () => {
    getUserFromApi.reset();
    userFromCookieCache = null;
};
export const getCookie = () => getCookieByName(cookieName);
export const getUrl = () => profileRoot;
export const getUserFromApiWithRefreshedCookie = () => {
    const endpoint = `${idApiRoot}/user/me`;
    const data = new URLSearchParams();
    data.append('refreshCookie', 'true');
    return fetch(endpoint, {
        mode: 'cors',
        credentials: 'include',
        body: data.toString(),
    }).then((resp) => resp.json());
};
export const redirectTo = (url) => {
    window.location.assign(url);
};
export const getUserOrSignIn = (componentId, paramUrl) => {
    let returnUrl = paramUrl;
    if (isUserLoggedIn()) {
        return getUserFromCookie();
    }
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- not sure what it would do
    returnUrl = encodeURIComponent(returnUrl || document.location.href);
    const url = [
        getUrl() || '',
        '/signin?returnUrl=',
        returnUrl,
        '&',
        createAuthenticationComponentEventParams(componentId),
    ].join('');
    redirectTo(url);
};
export const hasUserSignedOutInTheLast24Hours = () => {
    const cookieData = getCookieByName(signOutCookieName);
    if (cookieData) {
        return (Math.round(new Date().getTime() / 1000) <
            parseInt(cookieData, 10) + 86400);
    }
    return false;
};
export const shouldAutoSigninInUser = () => {
    const signedInUser = !!getCookieByName(cookieName);
    const checkFacebook = !!storage.local.get(fbCheckKey);
    return (!signedInUser && !checkFacebook && !hasUserSignedOutInTheLast24Hours());
};
export const getUserEmailSignUps = () => {
    const user = getUserFromCookie();
    if (user) {
        const endpoint = `${idApiRoot}/useremails/${user.id}`;
        const request = fetch(endpoint, {
            mode: 'cors',
            credentials: 'include',
        }).then((resp) => resp.json());
        return request;
    }
    return Promise.resolve(null);
};
export const sendValidationEmail = () => {
    const defaultReturnEndpoint = '/email-prefs';
    const endpoint = `${idApiRoot}/user/send-validation-email`;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- it will evaluate to false if undefined
    const returnUrl = getUrlVars().returnUrl
        ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- same as above
            decodeURIComponent(getUrlVars()?.returnUrl)
        : profileRoot + defaultReturnEndpoint;
    const data = new URLSearchParams();
    data.append('method', 'post');
    data.append('returnUrl', returnUrl);
    const request = fetch(endpoint, {
        mode: 'cors',
        credentials: 'include',
        body: data.toString(),
    });
    return request;
};
export const updateUsername = (username) => {
    const endpoint = `${idApiRoot}/user/me`;
    const data = {
        publicFields: {
            username,
            displayName: username,
        },
    };
    const request = fetch(endpoint, {
        mode: 'cors',
        method: 'POST',
        body: JSON.stringify(data),
        credentials: 'include',
    });
    return request;
};
export const getAllConsents = () => {
    const endpoint = '/consents';
    const url = idApiRoot + endpoint;
    return fetchJson(url, {
        mode: 'cors',
        method: 'GET',
        headers: { Accept: 'application/json' },
    });
};
export const getAllNewsletters = () => {
    const endpoint = '/newsletters';
    const url = idApiRoot + endpoint;
    return fetchJson(url, {
        mode: 'cors',
        method: 'GET',
        headers: { Accept: 'application/json' },
    });
};
export const getSubscribedNewsletters = () => {
    const endpoint = '/users/me/newsletters';
    const url = idApiRoot + endpoint;
    return fetchJson(url, {
        mode: 'cors',
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'include',
    }) // assert unknown -> NewslettersResponse
        .then((json) => {
        if (json?.result?.subscriptions) {
            return json.result.subscriptions.map((sub) => sub.listId);
        }
        return [];
    })
        .catch(() => []);
};
export const setConsent = (consents) => fetch(`${idApiRoot}/users/me/consents`, {
    method: 'PATCH',
    credentials: 'include',
    mode: 'cors',
    body: JSON.stringify(consents),
}).then((resp) => {
    if (resp.ok)
        return Promise.resolve();
    return Promise.reject();
});
export const getUserData = () => fetchJson(`${idApiRoot}/user/me`, {
    method: 'GET',
    mode: 'cors',
    credentials: 'include',
});
